import {NgModule} from '@angular/core';
import {FuseNavigationService} from '@fuse/components/navigation/navigation.service';
import {AdvisorProfileModel} from '@models/api/advisor-profile/advisor-profile.model';
import {AdvisorBusinessInfoModel} from '@models/api/advisor/advisor-business-info.model';
import {UserAppFeatureMatrix} from '@models/api/user/user-app-feature.matrix';
import {ApiAdvisorProfileService} from '@services/api/api-advisor-profile.service';
import {ApiAdvisorService} from '@services/api/api-advisor.service';
import {ApiUserService} from '@services/api/api-user.service';
import {FileDownloaderService} from '@services/file-downloader.service';
import {NotificationsService} from 'angular2-notifications';
import {environment} from 'environments/environment';
import {AuthService} from './auth.service';


@NgModule({})
export class UserInfoModule {
    static userAppFeatureMatrix: UserAppFeatureMatrix = {features: {}};

    private _selectedLang: any;

    private profileAvatarImage = '';
    private userAvatarImage = '';
    private primaryProfile: AdvisorProfileModel = null;
    private advisorBusinessInfo: AdvisorBusinessInfoModel = null;

    get selectedLang(): any {
        return this._selectedLang;
    }

    set selectedLang(value: any) {
        this._selectedLang = value;
    }

    constructor(
        private authService: AuthService,
        private apiAdvisorService: ApiAdvisorService,
        private apiAdvisorProfileService: ApiAdvisorProfileService,
        private apiUserService: ApiUserService,
        private fileDownloaderService: FileDownloaderService,
        private notificationsService: NotificationsService,
        private fuseNavigationService: FuseNavigationService,
    ) {
        this.authService.canActivateProtectedRoutes$.subscribe(value => {
            if (value) {
                this.authService.isDoneLoading$.subscribe(() => {
                    this.fuseNavigationService.updateNavigationItem(`main_group`, {hidden: this.isNE});
                    this.fuseNavigationService.updateNavigationItem(`client-pre-analyza`, {hidden: environment.production});
                    this.fuseNavigationService.updateNavigationItem(`devgroup`, {hidden: environment.production});

                    if (this.isCREASOFT) {
                        this.fuseNavigationService.updateNavigationItem(`client-pre-analyza`, {hidden: true});
                        this.fuseNavigationService.updateNavigationItem(`devgroup`, {hidden: true});

                        this.fuseNavigationService.updateNavigationItem(`client-financni-analyza`, {hidden: true});
                        this.fuseNavigationService.updateNavigationItem(`service-introduction`, {hidden: true});
                    }
                });

                this.downloadPrimaryProfile();
                this.downloadProfileAvatar();
                this.downloadUserAvatar();
                this.downloadFeatures();
            }
        });
    }

    public static isFeatureAvailable(code: string): boolean {
        if (UserInfoModule.userAppFeatureMatrix && UserInfoModule.userAppFeatureMatrix.features.hasOwnProperty(code)) {
            return UserInfoModule.userAppFeatureMatrix.features[code];
        }

        return false;
    }

    private downloadFeatures(): void {
        this.apiUserService.getAppFeatureMatrix()
            .subscribe((r) => {
                UserInfoModule.userAppFeatureMatrix = r;
                if (!environment.production) {
                    console.table(r.features);
                }

                this.fuseNavigationService.updateNavigationItem('client-new', {hidden: !UserInfoModule.isFeatureAvailable('client-new-candidate')});
                this.fuseNavigationService.updateNavigationItem('client-import', {hidden: !UserInfoModule.isFeatureAvailable('client-umd-import')});
                this.fuseNavigationService.updateNavigationItem('invest-group', {hidden: !UserInfoModule.isFeatureAvailable('investment-garant-approval')});
            }, (e) => {
                this.notificationsService.warn('Upozornění', 'Došlo k chybě při stahování uživatelských funkcionalit. Aplikace nemusí pracovat správně.');
            }, () => {
            });
    }

    private downloadProfileAvatar(): void {
        if (this.profileAvatarImage === '') {
            this.fileDownloaderService.downloadDataAsBase64(`${environment.BaseApiUrl}/Advisor/GetAvatar`)
                .subscribe((res) => {
                    this.profileAvatarImage = res;
                });
        }
    }

    private downloadUserAvatar(): void {
        if (this.profileAvatarImage === '') {
            this.fileDownloaderService.downloadDataAsBase64(`${environment.BaseApiUrl}/UserSession/Avatar`)
                .subscribe((res) => {
                    this.userAvatarImage = res;
                });
        }
    }

    private downloadPrimaryProfile(): void {
        this.apiAdvisorProfileService.getPrimary().subscribe(
            (data) => {
                this.primaryProfile = data;
                this.apiAdvisorService.getAdvisorBusinessInfo(data.id)
                    .subscribe(
                        (advisorBusinessInfo) => {
                            this.advisorBusinessInfo = advisorBusinessInfo;
                        }
                    );
            });
    }

    public logoutUser(): void {
        this.authService.logout();
    }

    public get advisorId(): number {
        return this.primaryProfile == null ? null : this.primaryProfile.id;
    }

    public get personalIdentifier(): string {
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['personal_identifier'];
    }

    public get userFullName(): string {
        if (this.primaryProfile != null) {
            return `${this.primaryProfile.firstName} ${this.primaryProfile.lastName}`;
        }
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['name'];
    }

    public get userGivenName(): string {
        if (this.primaryProfile != null) {
            return `${this.primaryProfile.firstName}`;
        }
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['given_name'];
    }

    public get userFamilyName(): string {
        if (this.primaryProfile != null) {
            return `${this.primaryProfile.lastName}`;
        }
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['family_name'];
    }

    public get userEmail(): string {
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['email'];
    }

    public get profileAvatar(): string {
        return this.profileAvatarImage;
    }

    public get userAvatar(): string {
        return this.userAvatarImage;
    }

    public get advisorIdentificationNumber(): string {
        if (this.advisorBusinessInfo != null) {
            return this.advisorBusinessInfo.orgId;
        }
        return '...';
    }

    public get advisorBusinessAddress(): string {
        if (this.advisorBusinessInfo != null) {
            const addressInfos: string[] = [];
            if (this.advisorBusinessInfo.businessPlaceAddress.streetName && !this.advisorBusinessInfo.businessPlaceAddress.streetName.isEmpty()) {
                addressInfos.push(this.advisorBusinessInfo.businessPlaceAddress.streetName);
            }
            if (this.advisorBusinessInfo.businessPlaceAddress.streetNumber && !this.advisorBusinessInfo.businessPlaceAddress.streetNumber.isEmpty()) {
                addressInfos.push(this.advisorBusinessInfo.businessPlaceAddress.streetNumber);
            }
            if (this.advisorBusinessInfo.businessPlaceAddress.postalCode && !this.advisorBusinessInfo.businessPlaceAddress.postalCode.isEmpty()) {
                addressInfos.push(this.advisorBusinessInfo.businessPlaceAddress.postalCode);
            }
            if (this.advisorBusinessInfo.businessPlaceAddress.city && !this.advisorBusinessInfo.businessPlaceAddress.city.isEmpty()) {
                addressInfos.push(this.advisorBusinessInfo.businessPlaceAddress.city);
            }

            return addressInfos.join(`, `);
        }
        return '...';
    }

    /**
     * poradce
     */
    public get isAR(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims[`user_role`] === `AR`;
    }

    /**
     * asistentka
     */
    public get isAA(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims[`user_role`] === `AA`;
    }

    /**
     * admin
     */
    public get isAN(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims[`user_role`] === `AN`;
    }

    /**
     * backoffice - aktualne centrala
     */
    public get isBO(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims[`user_role`] === `BO`;
    }

    /**
     * novy poradce
     */
    public get isNE(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims[`user_role`] === `NE`;
    }

    public get isCREASOFT(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims[`advisor_broker`] === `CREASOFT`;
    }

}
